(ns frontend.view.ai-summary-library.event
  "State and transport lifecycle for the AI Summary Library page."
  (:require
   [clojure.string :as string]
   [frontend.logging :as logging]
   [frontend.ui.ai-summary-configuration.model :as configuration-model]
   [frontend.ui.ai-summary-configuration.upload :as configuration-upload]
   [frontend.view.ai-summary-library.model :as model]
   [fx.ajax :as ajax]
   [oops.core :as oops]
   [re-frame.core :as rf]))

(defn- path
  [& keys]
  (into model/root-path keys))

(rf/reg-event-fx
 ::initialize
 (fn [{db :db} _]
   {:db (assoc-in db model/root-path (model/initial-state))
    :fx [[:dispatch [::load-configurations]]]}))

(rf/reg-event-db
 ::dispose
 (fn [db _]
   (assoc-in db model/root-path nil)))

(rf/reg-event-fx
 ::load-configurations
 (fn [{db :db} [_ preferred-id]]
   {:db (assoc-in db (path :load-state) :loading)
    ::ajax/send
    {:operation :ai-summary-configuration/list
     :body {}
     :on-ok [::configurations-loaded preferred-id]
     :on-error [::request-failed :list nil]}}))

(defn- configurations-loaded-fx
  [{db :db} [_ preferred-id response]]
  (let [configurations (get-in response [:body :configurations])
        current-id (get-in db (path :selected-configuration-id))
        available-ids (set (map :id configurations))
        selected-id (or (when (contains? available-ids preferred-id)
                          preferred-id)
                        (when (contains? available-ids current-id)
                          current-id)
                        (:id (first configurations)))
        next-db (-> db
                    (assoc-in (path :load-state) :ready)
                    (assoc-in (path :configurations) configurations)
                    (assoc-in (path :selected-configuration-id) selected-id))]
    (cond-> {:db next-db}
      selected-id
      (assoc :fx [[:dispatch [::select-configuration selected-id]]]))))

(rf/reg-event-fx
 ::configurations-loaded
 configurations-loaded-fx)

(rf/reg-event-fx
 ::select-configuration
 (fn [{db :db} [_ configuration-id]]
   {:db (-> db
            (assoc-in (path :selected-configuration-id) configuration-id)
            (assoc-in (path :detail-state) :loading)
            (assoc-in (path :configuration-detail) nil)
            (assoc-in (path :mode) :selected)
            (assoc-in (path :draft) nil)
            (assoc-in (path :saved-draft) nil)
            (assoc-in (path :copy-source-name) nil)
            (assoc-in (path :save-state) :idle)
            (assoc-in (path :uploading-field) nil))
    ::ajax/send
    {:operation :ai-summary-configuration/get
     :body {:configuration-id configuration-id}
     :on-ok [::configuration-loaded configuration-id]
     :on-error [::request-failed :detail configuration-id]}}))

(rf/reg-event-db
 ::configuration-loaded
 (fn [db [_ expected-id response]]
   (if (= expected-id (get-in db (path :selected-configuration-id)))
     (let [configuration (get-in response [:body :configuration])
           draft (configuration-model/configuration-detail->draft configuration)]
       (-> db
           (assoc-in (path :detail-state) :ready)
           (assoc-in (path :configuration-detail) configuration)
           (assoc-in (path :draft) draft)
           (assoc-in (path :saved-draft) draft)
           (assoc-in (path :save-state) :idle)))
     db)))

(rf/reg-event-db
 ::set-query
 (fn [db [_ query]]
   (assoc-in db (path :query) query)))

(rf/reg-event-db
 ::create-new
 (fn [db _]
   (let [draft (configuration-model/blank-draft)]
     (-> db
         (assoc-in (path :selected-configuration-id) nil)
         (assoc-in (path :detail-state) :ready)
         (assoc-in (path :configuration-detail)
                   {:owned-by-requester? true})
         (assoc-in (path :mode) :create)
         (assoc-in (path :draft) draft)
         (assoc-in (path :saved-draft) nil)
         (assoc-in (path :copy-source-name) nil)
         (assoc-in (path :save-state) :idle)
         (assoc-in (path :uploading-field) nil)))))

(defn- prepare-copy-db
  [db _]
  (let [draft (get-in db (path :draft))
        source-name (:configuration-name draft)]
    (-> db
        (assoc-in (path :mode) :copy)
        (assoc-in (path :draft)
                  (configuration-model/copy-draft draft ""))
        (assoc-in (path :saved-draft) nil)
        (assoc-in (path :copy-source-name) source-name)
        (assoc-in (path :save-state) :idle))))

(rf/reg-event-db
 ::prepare-copy
 prepare-copy-db)

(rf/reg-event-fx
 ::cancel-edit
 (fn [{db :db} _]
   (if-let [configuration-id
            (get-in db (path :selected-configuration-id))]
     {:fx [[:dispatch [::select-configuration configuration-id]]]}
     {:db (-> db
              (assoc-in (path :detail-state) :idle)
              (assoc-in (path :configuration-detail) nil)
              (assoc-in (path :draft) nil)
              (assoc-in (path :copy-source-name) nil)
              (assoc-in (path :mode) :selected))})))

(rf/reg-event-db
 ::update-draft
 (fn [db [_ field value]]
   (let [db (-> db
                (assoc-in (path :draft field) value)
                (assoc-in (path :save-state) :idle))]
     (case field
       :configuration-access-level
       (if (= value
              :ai-summary-configuration.access-level/restricted)
         db
         (assoc-in db (path :draft :use-access-ids) #{}))

       :applicable-pipeline-ids
       (if (seq value)
         (update-in db
                    (path :draft :default-pipeline-ids)
                    (fn [default-ids]
                      (set (filter (set value) default-ids))))
         db)

       db))))

(rf/reg-event-db
 ::toggle-source
 (fn [db [_ source selected?]]
   (-> db
       (update-in (path :draft :selected-data-sources)
                  (fn [selected]
                    ((if selected? conj disj) (set selected) source)))
       (assoc-in (path :save-state) :idle))))

(rf/reg-event-db
 ::clear-document
 (fn [db [_ field]]
   (case field
     :style-guideline-document
     (-> db
         (assoc-in (path :draft :style-guideline-document-id) nil)
         (assoc-in (path :draft :style-guideline-document-filename) nil)
         (assoc-in (path :save-state) :idle))

     :document-template
     (-> db
         (assoc-in (path :draft :document-template-id) nil)
         (assoc-in (path :draft :document-template-filename) nil)
         (assoc-in (path :save-state) :idle))

     db)))

(rf/reg-event-fx
 ::upload-document
 (fn [{db :db} [_ field file]]
   (let [filename (when file (oops/oget file "name"))
         template? (= field :document-template)
         valid? (and filename
                     (or (not template?)
                         (string/ends-with? (string/lower-case filename)
                                            ".docx")))]
     (if-not valid?
       {:db (-> db
                (assoc-in (path :uploading-field) nil)
                (assoc-in (path :save-state) :invalid-template))}
       {:db (-> db
                (assoc-in (path :uploading-field) field)
                (assoc-in (path :save-state) :idle))
        ::configuration-upload/document
        {:file file
         :on-success [::document-uploaded field filename]
         :on-error [::document-upload-failed field]}}))))

(rf/reg-event-db
 ::document-uploaded
 (fn [db [_ field filename attachment]]
   (if (= field (get-in db (path :uploading-field)))
     (let [attachment-id (:db/id attachment)
           attachment-filename (or (:attachment/filename attachment) filename)]
       (case field
         :style-guideline-document
         (-> db
             (assoc-in (path :draft :style-guideline-source)
                       :ai-summary-configuration.style-guideline-source/document)
             (assoc-in (path :draft :style-guideline-document-id) attachment-id)
             (assoc-in (path :draft :style-guideline-document-filename)
                       attachment-filename)
             (assoc-in (path :draft :style-reference-url) nil)
             (assoc-in (path :uploading-field) nil))

         :document-template
         (-> db
             (assoc-in (path :draft :document-template-id) attachment-id)
             (assoc-in (path :draft :document-template-filename)
                       attachment-filename)
             (assoc-in (path :uploading-field) nil))

         (assoc-in db (path :uploading-field) nil)))
     db)))

(rf/reg-event-db
 ::document-upload-failed
 (fn [db [_ field error]]
   (if (= field (get-in db (path :uploading-field)))
     (do
       (logging/error "AI summary library document upload failed" error)
       (-> db
           (assoc-in (path :uploading-field) nil)
           (assoc-in (path :save-state) :upload-failed)))
     db)))

(defn- save-fx
  [{db :db} _]
  (let [state (get-in db model/root-path)
        request (model/save-request state)]
    (when request
      {:db (assoc-in db (path :save-state) :saving)
       ::ajax/send
       (merge request
              (if (= :entity/create-entity (:operation request))
                {:on-ok [::configuration-created]
                 :on-error [::request-failed :save nil]}
                {:on-ok [::configuration-updated
                         (:selected-configuration-id state)]
                 :on-error [::request-failed
                            :save
                            (:selected-configuration-id state)]}))})))

(rf/reg-event-fx
 ::save
 save-fx)

(rf/reg-event-fx
 ::configuration-created
 (fn [{db :db} [_ response]]
   (let [configuration-id (get-in response [:body :db/id])]
     {:db (assoc-in db (path :save-state) :saved)
      :fx [[:dispatch [::load-configurations configuration-id]]]})))

(rf/reg-event-fx
 ::configuration-updated
 (fn [{db :db} [_ configuration-id _response]]
   {:db (assoc-in db (path :save-state) :saved)
    :fx [[:dispatch [::load-configurations configuration-id]]]}))

(rf/reg-event-fx
 ::delete-configuration
 (fn [{db :db} [_ configuration-id]]
   {:db (assoc-in db (path :save-state) :deleting)
    ::ajax/send
    {:operation :entity/disable!
     :body {:entity-id configuration-id}
     :on-ok [::configuration-deleted configuration-id]
     :on-error [::request-failed :delete configuration-id]}}))

(rf/reg-event-fx
 ::configuration-deleted
 (fn [{db :db} [_ configuration-id _response]]
   (if (= configuration-id
          (get-in db (path :selected-configuration-id)))
     {:db (-> db
              (assoc-in (path :selected-configuration-id) nil)
              (assoc-in (path :detail-state) :idle)
              (assoc-in (path :configuration-detail) nil)
              (assoc-in (path :draft) nil)
              (assoc-in (path :save-state) :idle))
      :fx [[:dispatch [::load-configurations]]]}
     {:fx [[:dispatch [::load-configurations]]]})))

(rf/reg-event-db
 ::request-failed
 (fn [db [_ phase expected-id error]]
   (let [selected-id (get-in db (path :selected-configuration-id))]
     (if (and expected-id (not= expected-id selected-id))
       db
       (do
         (logging/error "AI summary library request failed"
                        {:phase phase
                         :configuration-id expected-id
                         :error error})
         (case phase
           :list (assoc-in db (path :load-state) :failed)
           :detail (assoc-in db (path :detail-state) :failed)
           (assoc-in db (path :save-state) :failed)))))))
